import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import "./MemberForm.css";
import MemberSearch from "../../components/MemberSearch";
import MembershipMoto from "../../components/MembershipMoto";
import districtData from "../../assets/districts.json";
import { createMemberApi, apiDelete, apiGet } from "../../Services";
import { useSearchParams } from "react-router-dom";

function MemberForm() {
  // const [states, setStates] = useState([]);
  const states = [
    "Andaman and Nicobar",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    profession: "",
    country: "India",
    state: "",
    district: "",
    block: "",
    pin: "",
    aadhaar_no: "",
    mobile_no: "",
    vehicle_no: "",
    email: "",
    photo: null,
    aadhaarCard: null,
  });
  const [check, setCheck] = useState(false);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [transactionDetails, setTransactionDetails] = useState({});
  const receiptRef = useRef();
  const transactionId = searchParams.get("paymentStatus");

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [transactionIdOf, setTransactionIdOf] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://mercury.phonepe.com/web/bundle/checkout.js";

    script.onload = () => {
      setIsScriptLoaded(true);
    };
    script.onerror = () => {
      console.error("PhonePe script failed to load.");
      toast.error("Payment gateway failed to load. Please refresh.");
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (transactionId) {
      setLoading(true);
      handlePaymentStatus();
    }
  }, [transactionId]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setErrors({ ...errors, [name]: "" });
  };

  // Handle state change → update districts
  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    // setFormData({ state: selectedState, district: "", block: "" });
    setFormData((prev) => ({
      ...prev,
      state: selectedState,
      district: "",
      block: "",
    }));

    const filteredDistricts = districtData.districts.filter(
      (d) => d.state === selectedState
    );
    setDistricts(filteredDistricts);
    setBlocks([]);
  };

  // Handle district change → load blocks JSON dynamically
  const handleDistrictChange = async (e) => {
    const selectedDistrict = e.target.value;
    setFormData((prev) => ({
      ...prev,
      district: selectedDistrict,
      block: "",
    }));

    if (!formData.state) return;

    try {
      // Dynamically import the JSON file for blocks
      const blocksFile = await import(
        `../../assets/BlocksList/${formData.state.replace(/\s/g, "")}.json`
      );

      const districtBlocks = blocksFile[selectedDistrict] || [];
      setBlocks(districtBlocks);
    } catch (error) {
      console.error("Block list not found for this state:", error);
      setBlocks([]);
    }
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.name?.trim()) newErrors.name = "Name is required.";
    if (!formData.dob) newErrors.dob = "Date of Birth is required.";
    if (!formData.profession?.trim())
      newErrors.profession = "Profession is required.";
    if (!formData.state) newErrors.state = "State is required.";
    if (!formData.district) newErrors.district = "District is required.";
    if (!formData.block) newErrors.block = "Block is required.";
    if (!/^[0-9]{6}$/.test(formData.pin))
      newErrors.pin = "Pin Code must be 6 digits.";
    if (!/^[0-9]{12}$/.test(formData.aadhaar_no))
      newErrors.aadhaar_no = "Aadhaar must be 12 digits.";
    if (!/^[0-9]{10}$/.test(formData.mobile_no))
      newErrors.mobile_no = "Mobile number must be 10 digits.";
    if (!formData.photo) newErrors.photo = "Photo is required.";
    if (!formData.aadhaarCard)
      newErrors.aadhaarCard = "Aadhaar card is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix errors before proceeding.");
      return false;
    }
    return true;
  };

  const handleReview = () => {
    if (validate()) {
      setShowModal(true);
    }
  };

  const handleSubmit = async () => {
    if (!isScriptLoaded) {
      toast.error(
        "Payment gateway is still loading. Please try again in a moment."
      );
      return;
    }

    try {
      setLoading(true);
      setShowModal(false);

      // const { photo, aadhaarCard, ...dataWithoutFiles } = formData;
      const processedData = {
        ...formData,
        name: formData.name.trim().toUpperCase(),
        profession: formData.profession.trim().toUpperCase(),
        vehicle_no: formData.vehicle_no.trim().toUpperCase(),
      };
      const { photo, aadhaarCard, ...dataWithoutFiles } = processedData;

      const res = await createMemberApi(dataWithoutFiles, photo, aadhaarCard);
      console.log(res.data);
      const status = res.data.status;
      console.log(status);
      if (status === "success") {
        let tokenUrl = res.data.response.redirectUrl;
        // setOrderId(res.data.transactionId);
        const transaction = res.data.transactionId;
        setTransactionIdOf(transaction);
        function callback(response) {
          if (response === "USER_CANCEL") {
            // toast.error("Payment cancelled!");
            handleCancel(transaction);
            return;
          } else if (response === "CONCLUDED") {
            handlePaymentStatus(transaction);
            return;
          }
        }

        if (window.PhonePeCheckout) {
          window.PhonePeCheckout.transact({
            tokenUrl,
            callback,
            type: "IFRAME",
          });
        }
      } else if (status === "error") {
        toast.error(res.data.message || "Failed to create member");
      } else {
        toast.error("Payment gateway not available!");
      }

      // const res = await createMemberApi(dataWithoutFiles, photo, aadhaarCard);

      // toast.success("Member created successfully!");
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to create member";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (transaction) =>{
    setLoading(true);
    apiDelete(`/api/payment/cancel/${transaction}`).then((res) => {
      if (res.data.success) {
        // toast.success("Payment cancelled!");
        toast.error("Payment cancelled!");
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const handlePaymentStatus = (transaction) => {
    setLoading(true);
    apiGet(`/api/payment/status/${transaction}`)
      .then((res) => {
        if (res.data.success) {
          // console.log(res.data);
          toast.success("Payment successful!");
          setTransactionDetails(res.data.response);
          // console.log(transactionDetails);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const printReceipt = () => {
    const printContents = receiptRef.current.innerHTML;
    const newWindow = window.open("", "", "width=800,height=600");

    newWindow.document.write(`
    <html>
      <head>
        <title>Payment Receipt</title>
        <link
          href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
          rel="stylesheet"
        />
        <link rel="stylesheet" href="/MemberForm.css" />
        <style>
          body { font-family: Poppins, sans-serif; padding: 20px; }
          h4, h5 { color: #1a5276; }
          .section-title { font-weight: 600; margin-top: 20px; }
          .data-field { margin-bottom: 12px; }
          label { font-weight: 600; color: #444; }
          p { margin: 0; }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);

    newWindow.document.close();

    // Wait for styles to load before printing
    newWindow.onload = () => {
      newWindow.print();
      newWindow.close();
    };
  };

  return (
    <div
      className="container my-4"
      style={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "14px",
        padding: "0 12px",
      }}
    >
      <Helmet>
        <title>Membership Application | Manav Adhikar Forum</title>
        <meta
          name="description"
          content="Apply for membership in our Human Rights Organization. Fill in your details, upload required documents, and join our mission."
        />
        <meta
          name="keywords"
          content="Manav Adhikar Forum, membership, human rights, equality, justice, India, MAF, awareness, campaigns"
        />
        <link rel="canonical" href="https://manavadhikarforum.in/Membership" />
      </Helmet>

      <Row className="align-items-stretch">
        {/* Left Side Placeholder */}
        <Col md={5} className="d-flex flex-column ">
          <div className="mb-3">
            <MemberSearch />
          </div>

          <div className="flex-grow-1 d-flex">
            <MembershipMoto className="w-100 h-100" />
          </div>
        </Col>

        {/* Right Side Form */}
        <Col md={7} className="d-flex top-margin-sm-container ">
          <div className="p-4 border-0 rounded-3 shadow-lg bg-white flex-grow-1">
            {/* <h4 className="mb-5 text-center ">Membership Application Form</h4> */}
            <div className="d-flex flex-column mb-4 text-center align-items-center">
              <h4 className="fw-bold " style={{ color: "#1a5276" }}>
                Membership Application Form
                <div
                  className="rounded-pill mt-2 bg-warning"
                  style={{
                    height: "5px",
                    width: "15%",
                    // backgroundColor: "#EC1F24",
                  }}
                ></div>
              </h4>
            </div>
            {loading ? (
              <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ minHeight: "500px" }}
              >
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2">
                  Please wait, we are processing your request...
                </p>
                <p className="text-muted mt-2">
                  <strong>Please don't close this window.</strong>
                </p>
              </div>
            ) : Object.keys(transactionDetails).length > 0 ? (
              <Card className="p-4 shadow">
                <h4>Payment Status</h4>
                {transactionDetails.state === "COMPLETED" ? (
                  <>
                    <p>
                      ✅ Payment Completed <br />
                      <strong>Transaction ID:</strong> {transactionIdOf}
                    </p>
                    <Button
                      variant="primary"
                      className="rounded-pill"
                      style={{ margin: "auto" }}
                      onClick={() => setShowModal(true)}
                    >
                      View Payment Receipt
                    </Button>
                  </>
                ) : (
                  <>
                    <p>⚠️ Payment Status: {transactionDetails.state}</p>
                    <p>Please contact us for support.</p>
                  </>
                )}
              </Card>
            ) : false ? (
              <div className="d-flex justify-content-center mt-5">
                <Card style={{ width: "100%" }} className="text-center">
                  <Card.Body>
                    <Card.Title as="h2" className="text-danger">
                      Application Paused 😔
                    </Card.Title>
                    <Card.Text>
                      Online membership applications are currently paused.
                      <br />
                      To become a member, please contact us directly for more
                      information.
                    </Card.Text>
                    <Card.Link href="mailto:manavadhikarforumindia@gmail.com">
                      Contact Us
                    </Card.Link>
                  </Card.Body>
                </Card>
              </div>
            ) : (
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Full Name*</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder="Max 26 characters allowed"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleChange}
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Date of Birth*</Form.Label>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={formData.dob}
                        onChange={handleChange}
                        isInvalid={!!errors.dob}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.dob}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="text"
                        name="profession"
                        placeholder="Profession*"
                        value={formData.profession}
                        onChange={handleChange}
                        isInvalid={!!errors.profession}
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.profession}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      name="state"
                      value={formData.state}
                      onChange={handleStateChange}
                      required
                    >
                      <option value="">-- Select State --</option>
                      {states.map((s, idx) => (
                        <option key={idx} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.state}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Select
                      className="top-margin-sm"
                      name="district"
                      value={formData.district}
                      onChange={handleDistrictChange}
                      required
                      disabled={!formData.state}
                    >
                      <option value="">-- Select District --</option>
                      {districts.map((d, idx) => (
                        <option key={idx} value={d.district}>
                          {d.district}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.district}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      className="top-margin-sm"
                      name="block"
                      value={formData.block}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          block: e.target.value,
                        }))
                      }
                      required
                      disabled={!formData.district}
                    >
                      <option value="">-- Select Block --</option>
                      {blocks.map((b, idx) => (
                        <option key={idx} value={b}>
                          {b}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.block}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      name="pin"
                      placeholder="Pin Code*"
                      value={formData.pin}
                      onChange={handleChange}
                      isInvalid={!!errors.pin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.pin}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      name="aadhaar_no"
                      className="top-margin-sm"
                      placeholder="Aadhaar No*"
                      value={formData.aadhaar_no}
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaar_no}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.aadhaar_no}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      name="mobile_no"
                      placeholder="WhatsApp Number*"
                      value={formData.mobile_no}
                      onChange={handleChange}
                      isInvalid={!!errors.mobile_no}
                    />
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      This number will be used for all communications.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                      {errors.mobile_no}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Email"
                      value={formData.email}
                      onBlur={handleChange}
                      onChange={handleChange}
                    />
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      Email helps us keep you updated.
                    </Form.Text>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={6}>
                    <Form.Control
                      type="text"
                      name="vehicle_no"
                      placeholder="Vehicle No"
                      value={formData.vehicle_no}
                      onChange={handleChange}
                    />
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Form.Label className="text-muted">
                      Upload Photo*
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="photo"
                      accept="image/*"
                      onChange={handleChange}
                      isInvalid={!!errors.photo}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.photo}
                    </Form.Control.Feedback>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="text-muted">
                      Upload Aadhaar Card*
                    </Form.Label>
                    <Form.Control
                      type="file"
                      name="aadhaarCard"
                      accept="image/*"
                      onChange={handleChange}
                      isInvalid={!!errors.aadhaarCard}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.aadhaarCard}
                    </Form.Control.Feedback>
                  </Col>
                </Row>

                <div className="mt-3 d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="fees-checkbox"
                    value={check}
                    onChange={() => setCheck(!check)}
                    style={{ width: "20px", height: "20px" }}
                    className="me-2"
                  />
                  <label
                    htmlFor="fees-checkbox"
                    style={{ fontSize: "12px" }}
                    className="text-muted"
                  >
                    I understand that the ₹1600 <strong>Donation</strong> fees
                    is non-refundable if my application is rejected.
                  </label>
                </div>

                <div className="text-center mt-4">
                  <Button
                    variant="primary"
                    onClick={handleReview}
                    // onClick={handleSubmit}
                    disabled={!check}
                  >
                    Review & Pay
                  </Button>
                </div>
              </Form>
            )}

            {!loading && (
              <div
                className="mt-4 p-3 rounded-3"
                style={{
                  background:
                    "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
                  border: "1px solid #dbeafe",
                }}
              >
                <h6 className="fw-bold mb-2" style={{ color: "#1a5276" }}>
                  Before You Submit
                </h6>
                <ul
                  className="mb-0 ps-3 text-muted"
                  style={{ fontSize: "13px" }}
                >
                  <li>Check all details carefully in the review step.</li>
                  <li>Keep your phone active for payment confirmation.</li>
                  <li>After payment, save/print your receipt for reference.</li>
                </ul>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Review Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="application-review-modal"
      >
        <Modal.Header closeButton className="modal-header-custom">
          <Modal.Title>
            <i className="bi bi-clipboard-check me-2"></i>

            {transactionId !== null
              ? "Payment Reciept"
              : "Review Your Application"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" ref={receiptRef}>
          <div
            className={`review-progress-bar ${
              transactionIdOf !== null && "d-none"
            }`}
          >
            <div className="progress-step">1. Application</div>
            <div className="progress-step active">2. Review</div>
            <div className="progress-step">3. Payment</div>
          </div>

          <Row className="g-0">
            <Col md={8} className="p-4">
              <div className="section-title fs-5">
                <i className="bi bi-person-badge me-2"></i>
                Personal Information
              </div>

              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Full Name</label>
                    <p>{formData.name || "Not provided"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Date of Birth</label>
                    <p>{formData.dob || "Not provided"}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Profession</label>
                    <p>{formData.profession || "Not provided"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Mobile Number</label>
                    <p>{formData.mobile_no || "Not provided"}</p>
                  </div>
                </Col>
              </Row>

              <div className="data-field">
                <label>Email Address</label>
                <p>{formData.email || "Not provided"}</p>
              </div>

              <div className="section-title mt-4 fs-5">
                <i className="bi bi-geo-alt me-2"></i>
                Address Details
              </div>

              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>State</label>
                    <p>{formData.state || "Not provided"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>District</label>
                    <p>{formData.district || "Not provided"}</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Block</label>
                    <p>{formData.block || "Not provided"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>PIN Code</label>
                    <p>{formData.pin || "Not provided"}</p>
                  </div>
                </Col>
              </Row>

              <div className="section-title mt-4 fs-5">
                <i className="bi bi-card-checklist me-2"></i>
                Identification & Documents
              </div>

              <Row>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Aadhaar Number</label>
                    <p>{formData.aadhaar_no || "Not provided"}</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="data-field">
                    <label>Vehicle Number</label>
                    <p>{formData.vehicle_no || "Not provided"}</p>
                  </div>
                </Col>
              </Row>

              {transactionDetails?.amount && (
                <div className="section-title mt-4 fs-5">
                  <i className="bi bi-credit-card me-2"></i>
                  Payment Details
                </div>
              )}

              {transactionDetails?.amount && (
                <Row>
                  <Col sm={6}>
                    <div className="data-field">
                      <label>Amount</label>
                      <p>
                        {transactionDetails?.amount
                          ? `Rs. ${transactionDetails.amount / 100}`
                          : "Not available"}
                      </p>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="data-field">
                      <label>Transaction ID</label>
                      <p>{transactionIdOf || "Not available"}</p>
                    </div>
                  </Col>
                </Row>
              )}
            </Col>

            <Col md={4} className="documents-sidebar">
              <div className="p-3">
                <h6 className="text-center mb-3">
                  <i className="bi bi-images me-2"></i>
                  Uploaded Documents
                </h6>

                {formData.photo && (
                  <Card className="mb-3 document-card">
                    <Card.Header className="text-center">
                      Profile Photo
                    </Card.Header>
                    <Card.Body className="text-center p-2">
                      <img
                        src={URL.createObjectURL(formData.photo)}
                        alt="Profile"
                        className="uploaded-image"
                      />
                    </Card.Body>
                  </Card>
                )}

                {formData.aadhaarCard && (
                  <Card className="document-card">
                    <Card.Header className="text-center">
                      Aadhaar Card
                    </Card.Header>
                    <Card.Body className="text-center p-2">
                      <img
                        src={URL.createObjectURL(formData.aadhaarCard)}
                        alt="Aadhaar Card"
                        className="uploaded-image"
                      />
                    </Card.Body>
                  </Card>
                )}

                {!formData.photo && !formData.aadhaarCard && (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-inbox display-4"></i>
                    <p>No documents uploaded</p>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          {transactionIdOf ? (
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
            >
              Close
            </Button>
          ) : (
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Edit
            </Button>
          )}
          {transactionIdOf ? (
            <Button
              variant="success"
              onClick={printReceipt}
              className="d-flex align-items-center"
            >
              <bi className="bi bi-printer me-2"></bi>
              Print/Save
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={handleSubmit}
              // disabled={true}
              className="d-flex align-items-center"
            >
              <i className="bi bi-credit-card me-2"></i>
              Make Payment & Complete
              {/* Application Submission is Paused */}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MemberForm;
